import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { useT } from '../../i18n';

type Tone = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  tone: Tone;
  message: string;
  /** Milisegundos que le quedan en pantalla. Se congela al pasar por encima. */
  restante: number;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONO: Record<Tone, string> = {
  success: 'border-success/40 bg-success/10 text-success',
  error: 'border-danger/40 bg-danger/10 text-danger',
  info: 'border-primary/40 bg-primary/10 text-primary-soft',
};

/**
 * Un error se lee más despacio que una confirmación: casi siempre hay que
 * decidir algo a partir de él. Cuatro segundos bastan para «Guardado»; para
 * «No pudimos contactar con el servidor», no.
 */
const DURACION: Record<Tone, number> = {
  success: 4000,
  info: 4000,
  error: 8000,
};

const LATIDO = 250;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  /**
   * Mientras el puntero o el foco están dentro, el tiempo no corre.
   *
   * Es el requisito 2.2.1: contenido que desaparece solo tiene que poder
   * pausarse o alargarse. Sin esto, quien lee despacio —o quien está leyendo
   * el aviso con un lector de pantalla— ve cómo el texto se le va a mitad.
   */
  const pausado = useRef(false);

  useEffect(() => {
    if (toasts.length === 0) return;

    const reloj = setInterval(() => {
      if (pausado.current) return;
      setToasts((actuales) =>
        actuales
          .map((toast) => ({ ...toast, restante: toast.restante - LATIDO }))
          .filter((toast) => toast.restante > 0),
      );
    }, LATIDO);

    return () => clearInterval(reloj);
  }, [toasts.length]);

  const push = useCallback((tone: Tone, message: string) => {
    setToasts((actuales) => [
      ...actuales,
      { id: crypto.randomUUID(), tone, message, restante: DURACION[tone] },
    ]);
  }, []);

  const descartar = useCallback((id: string) => {
    setToasts((actuales) => actuales.filter((toast) => toast.id !== id));
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push('success', message),
      error: (message) => push('error', message),
      info: (message) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        `role="status"` con `aria-live="polite"`: quien usa lector de pantalla
        oye la confirmación sin que le interrumpa lo que estuviera haciendo.

        La región vive siempre en el documento, aunque esté vacía. Un contenedor
        que se monta con el primer aviso llega tarde: el lector necesita conocer
        la región *antes* de que cambie para anunciar el cambio.
      */}
      <div
        /*
         * Por encima de la barra inferior, no debajo de ella.
         *
         * Dentro de la aplicación hay una barra de pestañas fija que ocupa los
         * últimos ~64 px en todo lo que no sea `lg`, más la franja del gesto de
         * inicio del iPhone. Anclado a `bottom-4`, un aviso —«archivo subido»,
         * «no se pudo borrar»— aparecía justo detrás de ella y no lo veía
         * nadie: se anunciaba por voz para los lectores de pantalla y era
         * invisible para todos los demás.
         *
         * El ancho ya no sale de `100vw`. Esa unidad **incluye la barra de
         * desplazamiento**, así que en un navegador que la dibuja el aviso
         * sobresalía un par de píxeles por la derecha y la página entera se
         * podía arrastrar en horizontal. En un teléfono, además, un aviso de
         * borde a borde se lee mejor que uno estrecho pegado a una esquina.
         */
        className="pointer-events-none fixed inset-x-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-50 flex flex-col gap-2 sm:left-auto sm:right-4 sm:w-96 lg:bottom-4"
        role="status"
        aria-live="polite"
        onMouseEnter={() => (pausado.current = true)}
        onMouseLeave={() => (pausado.current = false)}
        onFocusCapture={() => (pausado.current = true)}
        onBlurCapture={() => (pausado.current = false)}
      >
        {toasts.map((toast) => (
          <Aviso key={toast.id} toast={toast} onDismiss={() => descartar(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function Aviso({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const t = useT();

  return (
    <div
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 text-sm shadow-card backdrop-blur-xl animate-slide-up',
        TONO[toast.tone],
      )}
    >
      <span className="flex-1">{toast.message}</span>
      {/*
        Cerrar a mano es la otra mitad de 2.2.1: si el tiempo se puede pausar,
        también tiene que poder terminarse. 24×24 px por 2.5.8.
      */}
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('common.dismiss')}
        className="-my-1 grid h-6 w-6 shrink-0 place-items-center rounded-lg text-current transition hover:bg-white/10"
      >
        <span aria-hidden="true">✕</span>
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast debe usarse dentro de <ToastProvider>');
  return context;
}
